import { getIdToken } from './auth.service';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface Product {
  _id: string;
  organizationId: string;
  name: string;
  slug: string;
  apiKey: string;
  apiKeyPrefix: string;
  environments: string[];
  isBlocked: boolean;
  isPaused: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductInput {
  organizationId: string;
  name: string;
  slug?: string;
  environments?: string[];
}

export interface UpdateProductInput {
  name?: string;
  environments?: string[];
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json.error || json.message || `Request failed with status ${response.status}`);
  }

  // Backend returns { success, data } format
  if ('data' in json) {
    return json.data as T;
  }

  return json as T;
}

export async function listProductsByOrganization(organizationId: string): Promise<Product[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/products/organization/${organizationId}`, {
    method: 'GET',
    headers,
  });
  return handleResponse<Product[]>(response);
}

export async function getProduct(id: string): Promise<Product> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/products/${id}`, {
    method: 'GET',
    headers,
  });
  return handleResponse<Product>(response);
}

export async function createProduct(input: CreateProductInput): Promise<Product> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/products`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });
  return handleResponse<Product>(response);
}

export async function updateProduct(id: string, input: UpdateProductInput): Promise<Product> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/products/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(input),
  });
  return handleResponse<Product>(response);
}

export async function deleteProduct(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/products/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Request failed with status ${response.status}`);
  }
}

export async function regenerateApiKey(id: string): Promise<Product> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/products/${id}/regenerate-key`, {
    method: 'POST',
    headers,
  });
  return handleResponse<Product>(response);
}

export async function pauseProduct(id: string): Promise<Product> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/products/${id}/pause`, {
    method: 'POST',
    headers,
  });
  return handleResponse<Product>(response);
}

export async function resumeProduct(id: string): Promise<Product> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/products/${id}/resume`, {
    method: 'POST',
    headers,
  });
  return handleResponse<Product>(response);
}
